/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useMemo } from 'react';
import type { NavigationContainer } from '../common/types.tsx';
import type {
	ContainerTypePermissions,
	GetViewUrl,
	JpdNavViewsContext,
	UseIsReadOnly,
	ViewsEntityLimitType,
	ViewsetsEntityLimitType,
} from './types.tsx';
import type { UseAnalyticsL2, UseAnalyticsL3, UseAnalyticsL4 } from './analytics/types.tsx';
import { JpdNavViewsProvider } from './context.tsx';
import { JpdNavViewsPermissions } from './permissions/index.tsx';

export type JpdNavViewsContainerProps<T extends NavigationContainer> = {
	children: React.ReactNode;
	name?: string;
	container: T;
	sectionsEnabled: boolean;
	containerId?: string;
	isProjectExpanded?: boolean;
	containerTypePermissions: ContainerTypePermissions;
	viewsEntityLimitType: ViewsEntityLimitType;
	viewsetsEntityLimitType?: ViewsetsEntityLimitType;
	getViewUrl: GetViewUrl<T>;
	useIsReadOnly?: UseIsReadOnly<T>;
	useAnalyticsL2: UseAnalyticsL2<T>;
	useAnalyticsL3: UseAnalyticsL3<T>;
	useAnalyticsL4: UseAnalyticsL4<T>;
};

const returnFalse = () => false;

export const JpdNavViewsContainer = <T extends NavigationContainer>({
	children,
	name,
	container,
	sectionsEnabled,
	containerTypePermissions,
	viewsEntityLimitType,
	viewsetsEntityLimitType,
	getViewUrl,
	useIsReadOnly = returnFalse,
	useAnalyticsL2,
	useAnalyticsL3,
	useAnalyticsL4,
}: JpdNavViewsContainerProps<T>): React.JSX.Element => {
	const value = useMemo(
		() =>
			// oxlint-disable-next-line typescript/consistent-type-assertions
			({
				name,
				container,
				sectionsEnabled,
				containerTypePermissions,
				viewsEntityLimitType,
				viewsetsEntityLimitType,
				getViewUrl,
				useIsReadOnly,
				useAnalyticsL2,
				useAnalyticsL3,
				useAnalyticsL4,
			}) as JpdNavViewsContext<NavigationContainer>,
		[
			name,
			container,
			sectionsEnabled,
			containerTypePermissions,
			viewsEntityLimitType,
			viewsetsEntityLimitType,
			getViewUrl,
			useIsReadOnly,
			useAnalyticsL2,
			useAnalyticsL3,
			useAnalyticsL4,
		],
	);

	return (
		<JpdNavViewsProvider value={value}>
			<JpdNavViewsPermissions>{children}</JpdNavViewsPermissions>
		</JpdNavViewsProvider>
	);
};
